import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
} from '@wix/editor-elements-integrations';
import type { LoginSocialBarSdkData } from '@wix/thunderbolt-components';
import { isExperimentOpen } from '@wix/editor-elements-common-utils';

import type {
  ILoginSocialBarMapperProps,
  LoginSocialBarDefinition,
  LoginSocialBarCSSVars,
} from '../LoginSocialBar.types';
import {
  CHOOSE_OPTION_TEXT_DEFAULT,
  defaultCompProps,
  Experiment,
  LOG_OUT_TEXT,
  LOG_OUT_TEXT_DEFAULT,
  LOGGED_OUT_TEXT,
  LOGGED_OUT_TEXT_DEFAULT,
  LOGIN_SOCIAL_BAR_NAMESPACE,
  OPTION_VALUE_TEXT,
} from '../constants';
import { getCssMapperOutput, getOldCssMapperOutput } from './mappers';
import { getSanitizedCommonCompData } from '../utils';

// TODO: after enabling CSS PB support, leave only params
// that are not customizable per breakpoint
export const props = withCompInfo<
  ILoginSocialBarMapperProps,
  LoginSocialBarDefinition,
  ILoginSocialBarMapperProps
>()(
  [
    'compData',
    'compProps',
    'fullNameCompType',
    'language',
    'compId',
    'translate',
    'trackClicksAnalytics',
    'experiments',
  ],
  (
    { compData, compProps, translate, experiments, ...compPropsRest },
    carmiData,
  ) => {
    const enableLoggingInStateInSSR = isExperimentOpen(
      experiments,
      Experiment.EnableLoggingInStateInSSR,
    );

    const enableCustomCSSVarsSupport = isExperimentOpen(
      experiments,
      Experiment.EnableCustomCSSVars,
    );

    const {
      loggedInMember,
      loggedOutMember,
      loggedInAvatarIcon,
      loggedInCustomAvatar,
      loggedOutAvatarIcon,
      loggedOutCustomAvatar,
      loggedInTextState,
      showLoggedInText,
    } = getSanitizedCommonCompData(compData);

    const loggedOutText =
      carmiData.loggedOutText === LOGGED_OUT_TEXT_DEFAULT
        ? translate(LOGIN_SOCIAL_BAR_NAMESPACE, LOGGED_OUT_TEXT) ||
          LOGGED_OUT_TEXT_DEFAULT
        : carmiData.loggedOutText;

    const logOutText =
      carmiData.logOutText === LOG_OUT_TEXT_DEFAULT
        ? translate(LOGIN_SOCIAL_BAR_NAMESPACE, LOG_OUT_TEXT) ||
          LOG_OUT_TEXT_DEFAULT
        : carmiData.logOutText;

    return {
      ...carmiData,
      ...compPropsRest,
      arrowShape: compProps.arrowShape ?? defaultCompProps.arrowShape,
      showBellIcon: compProps.showBellIcon ?? defaultCompProps.showBellIcon,
      menuItemsAlignment: compProps.dropDownTextAlignment,
      translations: {
        accountButtonAriaLabel: translate(
          'ariaLabels',
          'Login_Bar_AccountButton_AriaLabel',
        ),
        loggedOutText,
        logOutText,
        menuOptionValueText:
          translate(LOGIN_SOCIAL_BAR_NAMESPACE, OPTION_VALUE_TEXT) ||
          CHOOSE_OPTION_TEXT_DEFAULT,
      },
      displayMode: loggedInMember,
      loggedInAvatarIcon,
      loggedInCustomAvatar,
      loggedOutDisplayMode: loggedOutMember,
      loggedOutAvatarIcon,
      loggedOutCustomAvatar,
      showLoggedInText,
      loggedInTextState,
      loggedInText: compData.loggedInText,
      customLoggedInText: compData.customLoggedInText ?? '',
      enableLoggingInStateInSSR,
      enableCustomCSSVarsSupport,
    };
  },
);

export const css = withCompInfo<
  LoginSocialBarCSSVars,
  LoginSocialBarDefinition,
  LoginSocialBarCSSVars
>()(
  ['compProps', 'compData', 'styleProperties', 'experiments'],
  ({ compProps, compData, experiments, styleProperties }, carmiCss) => {
    const enableCustomCSSVarsSupport = isExperimentOpen(
      experiments,
      Experiment.EnableCustomCSSVars,
    );

    return enableCustomCSSVarsSupport
      ? getCssMapperOutput({
          compData,
          compProps,
          styleProperties,
          carmiCss,
        })
      : getOldCssMapperOutput({
          compProps,
          styleProperties,
          carmiCss,
        });
  },
);

export const sdkData = withCompInfo<
  LoginSocialBarSdkData,
  LoginSocialBarDefinition,
  LoginSocialBarSdkData
>()([], (_deps, carmiSdkData) => {
  return carmiSdkData;
});

export const stateRefs = withStateRefsValues([
  'memberDetails',
  'reportBi',
  'currentUrl',
  'navigateTo',
  'logout',
  'promptLogin',
]);

export default createComponentMapperModel({ props, css, sdkData, stateRefs });
